import { Button, LinearProgress, List, Typography, useMediaQuery, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddIcon from '@mui/icons-material/Add';
import DoubleArrow from '@mui/icons-material/DoubleArrow';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import NoItemBox from '../../../shared-components/NoItemBox';
import NotationResponseDialogV2 from '../../../shared-components/Notation/NotationResponseDialogV2';
import { INotation } from '../../../shared-interfaces/Notation/INotation';
import { INotationComment } from '../../../shared-interfaces/Notation/INotationComment';
import { ICoreUser } from '../../../shared-interfaces/User/ICoreUser';
import NotationService from '../../../shared-services/Notation/NotationService';
import { NotationType } from '../../../shared-types/Notation/NotationType';
import ResponseListItem, {
  ResponseListItemLoadingSkeleton,
} from './ResponseListItem';

interface IResponseContainer {
  notation: INotation;
  additionalAudience: ICoreUser[];
  setAdditionalAudience: (additionalAudience: ICoreUser[]) => void;
  alertStatus: 'Open' | 'Lowered' | null;
  setAlertStatus: (alertStatus: 'Open' | 'Lowered' | null) => void;
  setAssignedTo: (u: ICoreUser) => void;
  assignedTo?: ICoreUser;
}

const useStyles = makeStyles({
  heading: {
    fontWeight: 600,
  },
  buttonContainer: {
    padding: '4px 0',
  },
  responseButton: {
    marginRight: '8px',
  },
});

const ResponseContainer = (props: IResponseContainer) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const isLessThanSm = useMediaQuery(useTheme().breakpoints.down('md'));

  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [comments, setComments] = useState<INotationComment[]>([]);
  const [totalComments, setTotalComments] = useState(0);
  const [openLowerDialog, setOpenLowerDialog] = useState(false);
  const [openResponseDialog, setOpenResponseDialog] = useState(false);

  const getComments = useCallback(
    async (page: number) => {
      const comments = await NotationService.getComments({
        page: page,
        pageSize: 10,
        searchParameters: {
          successNotificationId: props.notation.id,
        },
      });
      return comments;
    },
    [props.notation.id],
  );

  useEffect(() => {
    if (props.notation.id) {
      getComments(1)
        .then(c => {
          setComments([...comments, ...c.items]);
          setTotalComments(c.totalItems);
        })
        .finally(() => {
          setInitialLoading(false);
        });
    }
  }, [props.notation]);

  return (
    <>
      <div>
        <Typography component="h4" className={classes.heading}>
          Responses:
        </Typography>
        <div className={classes.buttonContainer}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            className={classes.responseButton}
            onClick={() => setOpenResponseDialog(true)}
          >
            <AddIcon />
            Add Response
          </Button>
          {props.notation.notationType === NotationType.Alert &&
            props.notation.notationType === 'Alert' &&
            props.alertStatus === 'Open' && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                className={classes.responseButton}
                onClick={() => setOpenLowerDialog(true)}
              >
                <DoubleArrow style={{ transform: 'rotate(90deg)' }} />
                Lower Alert
              </Button>
            )}
        </div>
        <List>
          {initialLoading ? (
            [...Array(10)].map((e, i) => (
              <ResponseListItemLoadingSkeleton key={i} />
            ))
          ) : comments.length > 0 ? (
            <InfiniteScroll
              style={{
                margin: 0,
                padding: 0,
                position: 'relative',
                listStyle: 'none',
              }}
              element="ul"
              useWindow={true}
              loadMore={page => {
                setIsLoading(true);
                getComments(page + 1)
                  .then(c => {
                    setComments([...comments, ...c.items]);
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
              initialLoad={false}
              hasMore={comments.length < totalComments && !isLoading}
            >
              {comments.map((x: INotationComment, i) => {
                return (
                  <ResponseListItem
                    key={x.id}
                    comment={x}
                    notation={props.notation}
                    afterDelete={commentId => {
                      setComments(comments.filter(x => x.id !== commentId));
                      setTotalComments(c => {
                        return c - 1;
                      });
                    }}
                  />
                );
              })}
            </InfiniteScroll>
          ) : (
            <NoItemBox height={575} text="No Responses to Display" />
          )}
        </List>
        {isLoading && <LinearProgress aria-busy={true} />}
      </div>
      <NotationResponseDialogV2
        open={openResponseDialog}
        close={() => setOpenResponseDialog(false)}
        notation={props.notation}
        assignable={true}
        additionalAudience={props.additionalAudience}
        usedToLower={false}
        title="Add Reponse"
        transition={isLessThanSm}
        onComplete={(success, comments, aa, at) => {
          if (success) {
            if (comments) setComments(comments);
            if (aa) props.setAdditionalAudience(aa);
            if (at) props.setAssignedTo(at);
            enqueueSnackbar('Response Added', { variant: 'success' });
          } else {
            enqueueSnackbar('Failed to Add Response', {
              variant: 'error',
            });
          }
        }}
      />
      <NotationResponseDialogV2
        open={openLowerDialog}
        close={() => setOpenLowerDialog(false)}
        notation={props.notation}
        assignable={false}
        usedToLower={true}
        title="Lower Alert"
        transition={isLessThanSm}
        onComplete={(success, comments) => {
          if (success) {
            if (comments) setComments(comments);
            props.setAlertStatus('Lowered');
            enqueueSnackbar('Alert Lowered', { variant: 'success' });
          } else {
            enqueueSnackbar('Failed to Lower Alert', {
              variant: 'error',
            });
          }
        }}
      />
    </>
  );
};

export default ResponseContainer;
